/**
 * Authenticated axios client.
 *
 * Handles:
 *   - Attaching the current access token to every outbound request.
 *   - Auto-refreshing the token pair on a single 401, retrying the
 *     original request exactly once, then giving up (which the
 *     AuthContext converts into a logout).
 *   - Single-flight refresh: if ten requests all hit 401 at the same
 *     time, they queue on the SAME refresh call — we never fire
 *     multiple `/auth/refresh` requests in parallel.
 *
 * Tokens are never logged. The refresh-token value is only ever
 * transmitted as the request body of POST /auth/refresh.
 */

import axios from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
} from './tokenStorage.js';

const BASE_URL = (import.meta.env?.VITE_API_URL || '').replace(/\/+$/, '');

// Callback fired when a 401 → refresh cycle ends unrecoverably. The
// AuthContext registers itself as the listener so it can drop user
// state + redirect to /login. Kept out of the axios instance so the
// module has no React coupling and is trivial to test.
let onUnauthorized = null;
export function setUnauthorizedHandler(cb) { onUnauthorized = cb; }

// Single-flight refresh promise: set for the duration of an in-flight
// /auth/refresh call, so subsequent 401s wait on it instead of
// launching a second refresh. Cleared once the call resolves.
let refreshInFlight = null;

/**
 * Perform the token-rotation call. Returns the new access token on
 * success, throws on any failure (which the caller uses to trigger
 * logout). Uses a bare axios call — never the wrapped client — so it
 * cannot recursively 401 on itself.
 */
async function performRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('no_refresh_token');
  const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
  if (!data?.accessToken || !data?.refreshToken) {
    throw new Error('bad_refresh_response');
  }
  setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  return data.accessToken;
}

function refreshTokens() {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// ── Axios instance ──────────────────────────────────────────────────

const authClient = axios.create({ baseURL: BASE_URL });

authClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

authClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config;
    // Guard: retry only once per original request, and only for 401.
    if (status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      const newAccess = await refreshTokens();
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newAccess}`;
      return authClient(original);
    } catch (refreshErr) {
      clearTokens();
      if (typeof onUnauthorized === 'function') {
        try { onUnauthorized(); } catch { /* swallow — best-effort */ }
      }
      return Promise.reject(refreshErr);
    }
  },
);

// Test hook: reset the single-flight state so tests don't leak refresh
// promises across cases.
export function _resetForTests() {
  refreshInFlight = null;
  onUnauthorized = null;
}

export { authClient };
export default authClient;
